import React from 'react';
import { Slider } from 'react-semantic-ui-range';
import { formatTimestamp } from '../../utils';

interface ControlsProps {
  duration: number;
  togglePlay: () => void;
  onSeek: (e: any, time: number) => void;
  fullScreen: (fs: boolean) => void;
  toggleMute: () => void;
  showSubtitle: () => void;
  syncSelf: () => void;
  paused: boolean;
  muted: boolean;
  volume: number;
  subtitled: boolean;
  currentTime: number;
  setVolume: (volume: number) => void;
  disabled?: boolean;
  leaderTime?: number;
  isPauseDisabled?: boolean;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
  beta: boolean;
  roomPlaybackRate: number;
  isYouTube: boolean;
  setSubtitleMode: (mode: TextTrackMode, lang?: string) => void;
  isLiveHls: boolean;
  showProgressBar: boolean;
  isScreenShare?: boolean;
}

export class Controls extends React.Component<ControlsProps> {
  state = {
    showTimestamp: false,
    hoverTimestamp: 0,
    hoverPos: 0,
    dragging: false,
    draggingTime: null as number | null,
  };

  progressRef = React.createRef<HTMLDivElement>();

  onScrubberDown = (e: React.PointerEvent) => {
    if (this.props.disabled) return;
    if (!this.progressRef.current || this.props.duration === Infinity) return;

    this.setState({ dragging: true });
    window.addEventListener('pointermove', this.onScrubberMove);
    window.addEventListener('pointerup', this.onScrubberUp);

    this.handleScrub(e.clientX);
  };

  onScrubberMove = (e: PointerEvent) => {
    this.handleScrub(e.clientX);
  };

  onScrubberUp = () => {
    const { draggingTime } = this.state;
    if (draggingTime !== null && this.props.onSeek) {
      this.props.onSeek(null, draggingTime);
    }

    this.setState({ dragging: false, draggingTime: null });

    window.removeEventListener('pointermove', this.onScrubberMove);
    window.removeEventListener('pointerup', this.onScrubberUp);
  };

  handleScrub = (clientX: number) => {
    if (!this.progressRef.current || this.props.duration === Infinity) return;

    const rect = this.progressRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.min(Math.max(x / rect.width, 0), 1);
    const newTime = pct * this.props.duration;

    this.setState({
      hoverTimestamp: newTime,
      hoverPos: pct,
      draggingTime: newTime,
    });

    if (this.props.onSeek) {
      this.props.onSeek(null, newTime);
    }
  };

  onMouseOver = () => {
    this.setState({ showTimestamp: true });
  };

  onMouseOut = () => {
    this.setState({ showTimestamp: false });
  };

  onMouseMove = (e: React.MouseEvent) => {
    if (!this.progressRef.current || this.props.duration === Infinity) return;

    const rect = this.progressRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const max = rect.width;
    const pct = Math.min(Math.max(x / max, 0), 1);
    const target = pct * this.props.duration;

    this.setState({ hoverTimestamp: target, hoverPos: pct });
  };

  render() {
    const {
      togglePlay,
      onSeek,
      fullScreen,
      toggleMute,
      showSubtitle,
      syncSelf,
      duration,
      leaderTime,
      isPauseDisabled,
      disabled,
      subtitled,
      paused,
      muted,
      volume,
    } = this.props;

    const currentTime = this.state.draggingTime ?? this.props.currentTime;
    const isBehind = leaderTime && leaderTime - currentTime > 5;

    return (
      <div
        className={`flex items-center gap-2 bg-[#2D2D2DA6] p-3 rounded-xl absolute max-w-[95%] w-full z-50 text-white bottom-3 left-1/2 -translate-x-1/2 backdrop-blur-sm ${
          this.props.isScreenShare ? 'justify-end' : ''
        }`}
      >
        {/* Play/Pause */}
        {!this.props.isScreenShare && (
          <button
            className="text-4xl text-gradient-pink"
            disabled={disabled || isPauseDisabled}
            onClick={togglePlay}
          >
            {paused ? (
              <i className="ri-play-circle-fill"></i>
            ) : (
              <i className="ri-pause-circle-fill"></i>
            )}
          </button>
        )}

        {/* LIVE Indicator */}
        {!this.props.isScreenShare && (
          <div className="group relative">
            <h6 className="gradient-outline-btn hidden w-[250px] text-center !absolute bottom-full p-2 rounded group-hover:block">
              <span>
                {isBehind
                  ? 'Skip to the live stream position'
                  : "You're at the live stream position"}
              </span>
            </h6>
            <button
              className={`flex items-center gap-1 text-sm font-semibold ${
                isBehind ? 'text-gray-400' : 'text-red-500'
              }`}
              onClick={() => {
                if (this.props.isLiveHls) {
                  onSeek(null, Number.MAX_SAFE_INTEGER);
                } else {
                  syncSelf();
                }
              }}
            >
              <i className="ri-circle-fill text-[10px] max-md:hidden"></i>
              LIVE
            </button>
          </div>
        )}

        {/* Progress and Time */}
        {this.props.showProgressBar && (
          <div className="flex items-center w-full gap-2 max-md:flex-wrap max-md:justify-between max-md:gap-1">
            <h6 className="text-sm max-md:order-1">
              {formatTimestamp(currentTime)}
            </h6>

            <div
              className="w-full h-1 bg-gray-200 rounded-full relative"
              ref={this.progressRef}
              onMouseOver={this.onMouseOver}
              onMouseOut={this.onMouseOut}
              onMouseMove={this.onMouseMove}
              onClick={(e) => {
                if (duration < Infinity && !disabled) {
                  const rect =
                    this.progressRef.current!.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const pct = Math.min(Math.max(x / rect.width, 0), 1);
                  const time = pct * duration;
                  onSeek(null, time);
                }
              }}
            >
              <div
                style={{
                  width: `${(currentTime / duration) * 100}%`,
                }}
                className="absolute top-0 left-0 h-full duration-200 rounded-full bg-[#54c8ff]"
              />

              <button
                onPointerDown={this.onScrubberDown}
                style={{
                  left: `calc(${(currentTime / duration) * 100}% - 8px)`,
                }}
                disabled={this.props.disabled}
                className="absolute top-[-6px] size-4 bg-white rounded-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-100"
              />

              {duration < Infinity && this.state.showTimestamp && (
                <div
                  style={{
                    left: `calc(${this.state.hoverPos * 100}% - 27px)`,
                  }}
                  className="absolute bottom-4 pointer-events-none"
                >
                  <h6 className="gradient-outline-btn text-center !absolute bottom-full p-2 rounded">
                    {formatTimestamp(this.state.hoverTimestamp)}
                  </h6>
                </div>
              )}
            </div>

            <h6 className="text-sm max-md:order-2">
              {formatTimestamp(duration)}
            </h6>
          </div>
        )}

        {/* Volume */}
        {!this.props.isScreenShare && (
          <>
            <button className="text-white text-2xl" onClick={toggleMute}>
              {muted ? (
                <i className="ri-volume-mute-fill"></i>
              ) : (
                <i className="ri-volume-up-fill"></i>
              )}
            </button>

            <div className="w-24 shrink-0 max-md:hidden">
              <Slider
                value={volume}
                color={'blue'}
                disabled={muted}
                settings={{
                  min: 0,
                  max: 1,
                  step: 0.01,
                  onChange: (value: number) => {
                    if (value !== this.props.volume && !isNaN(value)) {
                      this.props.setVolume(value);
                    }
                  },
                }}
              />
            </div>
          </>
        )}

        {/* Fullscreen */}
        <button
          className="text-white text-2xl ml-auto"
          onClick={() => fullScreen(true)}
        >
          <i className="ri-fullscreen-line [.fake-fullscreen_&]:!hidden"></i>
          <i className="ri-fullscreen-exit-line !hidden [.fake-fullscreen_&]:!inline-block"></i>
        </button>
        {/* video speed */}
        {/* <div className="flex items-center mx-2">
          <div className="text-[10px] font-bold flex items-center">
            <Popup
              content={
                this.props.roomPlaybackRate === 0
                  ? 'Playing at a dynamic rate to keep you in sync'
                  : 'Playing at a manually selected rate'
              }
              trigger={
                <Icon
                  name="sync alternate"
                  loading={this.props.roomPlaybackRate === 0}
                />
              }
            />
            {this.props.playbackRate?.toFixed(2)}x
          </div>
          <Dropdown className={`-ml-2 ${styles.control}`} disabled={disabled}>
            <Dropdown.Menu>
              {[
                { key: 'Auto', text: 'Auto', value: 0 },
                { key: '0.25', text: '0.25x', value: 0.25 },
                { key: '0.5', text: '0.5x', value: 0.5 },
                { key: '0.75', text: '0.75x', value: 0.75 },
                { key: '1', text: '1x', value: 1 },
                { key: '1.25', text: '1.25x', value: 1.25 },
                { key: '1.5', text: '1.5x', value: 1.5 },
                { key: '1.75', text: '1.75x', value: 1.75 },
                { key: '2', text: '2x', value: 2 },
              ].map((item) => (
                <Dropdown.Item
                  key={item.key}
                  text={item.text}
                  onClick={() => this.props.setPlaybackRate(item.value)}
                  active={this.props.roomPlaybackRate === item.value}
                  disabled={disabled}
                />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div> */}

        {/* subtitle button */}
        {/* {this.props.isYouTube ? (
          <Dropdown icon="closed captioning outline large" disabled={disabled}>
            <Dropdown.Menu>
              {[
                { key: 'hidden', text: 'Off', value: 'hidden' },
                { key: 'en', text: 'English', value: 'showing' },
                { key: 'es', text: 'Spanish', value: 'showing' },
              ].map((item) => (
                <Dropdown.Item
                  key={item.key}
                  text={item.text}
                  disabled={disabled}
                  onClick={() =>
                    this.props.setSubtitleMode(
                      item.value as TextTrackMode,
                      item.key
                    )
                  }
                />
              ))}
            </Dropdown.Menu>
          </Dropdown>
        ) : (
          <Icon
            size="large"
            onClick={() => {
              showSubtitle();
            }}
            name={subtitled ? 'closed captioning' : 'closed captioning outline'}
            title="Captions"
          />
        )} */}

        {/* 
        <Icon
          size="large"
          onClick={() => fullScreen(false)}
          className={`rotate-90 ${styles.control} ${styles.action}`}
          name="window maximize outline"
          title="Theater Mode"
        /> */}
      </div>
    );
  }
}
